// KaziPeople Web — company admin: manage the bookmarks employees see and the coarse
// URL policy (allow/block domains, external browsing). Gated to browser:bookmarks.manage
// / browser:policy.manage. Backend: /api/v1/web/{bookmarks,policy}.
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Globe, Plus, Pencil, Trash2, GripVertical, Shield } from 'lucide-react';
import { Card } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { useHasAnyPermission } from '@/rbac/useRbac';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  listAllBookmarks, createBookmark, updateBookmark, deleteBookmark, getPolicy, updatePolicy,
} from '@/lib/webApi';
import type { Bookmark, BrowserPolicy } from '../lib/types';
import { faviconFor } from '../lib/urlPolicy';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';

const CATEGORIES = ['HR', 'Payroll', 'Government', 'Banking', 'Benefits', 'Training', 'Customers', 'Vendors', 'Internal', 'Other'];

type Draft = Partial<Bookmark> & { name: string; url: string };
const emptyDraft = (): Draft => ({ name: '', url: '', category: 'Other', enabled: true, openMode: 'external', visibilityType: 'tenant', sortOrder: 0 });

export const WebSettingsPage: React.FC = () => {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const loc = useLocation();
  // Precise browser manage perms (built-in HR or a custom role) OR company-admin (owner).
  const canManageBookmarks = useHasAnyPermission(['browser:bookmarks.manage', 'tenant:update:own']);
  const canManagePolicy = useHasAnyPermission(['browser:policy.manage', 'tenant:update:own']);
  const { data: bookmarks = [], isLoading } = useQuery({ queryKey: ['web', 'bookmarks', 'manage'], queryFn: listAllBookmarks, enabled: canManageBookmarks });
  const { data: policy } = useQuery({ queryKey: ['web', 'policy'], queryFn: getPolicy });

  const [editing, setEditing] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  // Force a refetch (not just mark-stale) and await it, so the list reflects the
  // write immediately — the add/edit dialog closes onto the updated table.
  const refresh = () => qc.invalidateQueries({ queryKey: ['web'], refetchType: 'all' });

  const saveBookmark = async () => {
    if (!editing) return;
    const name = editing.name.trim();
    const url = editing.url.trim();
    if (!name || !url) { toast.error('Name and URL are required'); return; }
    if (!/^https?:\/\//i.test(url)) { toast.error('URL must start with http:// or https://'); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await updateBookmark(editing.id, { name, url, category: editing.category, description: editing.description, enabled: editing.enabled, sortOrder: editing.sortOrder });
      } else {
        await createBookmark({ name, url, category: editing.category, description: editing.description, enabled: editing.enabled, sortOrder: editing.sortOrder });
      }
      toast.success('Bookmark saved');
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save bookmark'));
    } finally { setSaving(false); }
  };

  const removeBookmark = async (b: Bookmark) => {
    if (!(await confirm({ title: 'Remove bookmark', message: `Remove "${b.name}"?`, danger: true, confirmLabel: 'Remove' }))) return;
    try {
      await deleteBookmark(b.id);
      toast.success('Bookmark removed');
      await refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not remove bookmark'));
    }
  };

  return (
    <div className="space-y-6 p-1">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-500/20"><Globe className="h-5 w-5 text-primary-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">KaziPeople Web settings</h1>
          <p className="text-sm text-muted-foreground">Company bookmarks and the external-browsing policy for your team.</p>
          <button
            type="button"
            onClick={() => navigate(loc.pathname.replace(/\/settings\/web$/, '/web/privacy'))}
            className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition hover:text-foreground"
          >
            <Shield className="h-3.5 w-3.5" /> What your team can and can’t see
          </button>
        </div>
      </div>

      {/* Bookmarks */}
      {canManageBookmarks && (
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Company bookmarks</h2>
            <Button size="sm" onClick={() => setEditing(emptyDraft())}><Plus className="mr-1.5 h-4 w-4" /> Add bookmark</Button>
          </div>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : bookmarks.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No bookmarks yet. Add company resources your team should reach quickly.</p>
          ) : (
            <div className="divide-y divide-border rounded-lg border border-border">
              {bookmarks.map((b) => {
                const fav = faviconFor(safeHost(b.url));
                return (
                  <div key={b.id} className="flex items-center gap-3 px-3 py-2.5">
                    <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                    {fav ? <img src={fav} alt="" className="h-5 w-5 rounded" onError={(e) => (e.currentTarget.style.display = 'none')} /> : <Globe className="h-5 w-5 text-muted-foreground" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-foreground">{b.name}</span>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">{b.category}</span>
                        {!b.enabled && <span className="rounded bg-warning-soft px-1.5 py-0.5 text-[10px] font-medium text-warning-strong">disabled</span>}
                      </div>
                      <span className="block truncate text-xs text-muted-foreground">{b.url}</span>
                    </div>
                    <Button size="icon" variant="ghost" aria-label="Edit" onClick={() => setEditing({ ...b })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" aria-label="Remove" onClick={() => removeBookmark(b)}><Trash2 className="h-4 w-4 text-danger" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Policy */}
      {canManagePolicy && policy && <PolicyEditor policy={policy} onSaved={refresh} />}

      {/* Add/edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit bookmark' : 'Add bookmark'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="NMB Internet Banking" />
              </div>
              <div>
                <Label>URL</Label>
                <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://nmbbank.co.tz" inputMode="url" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <select className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Switch checked={!!editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />
                  <Label className="mb-0">Enabled</Label>
                </div>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="What this is for" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button disabled={saving} onClick={saveBookmark}>{editing?.id ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PolicyEditor: React.FC<{ policy: BrowserPolicy; onSaved: () => void }> = ({ policy, onSaved }) => {
  const [allowExternal, setAllowExternal] = useState(policy.allowExternal);
  const [downloadsAllowed, setDownloadsAllowed] = useState(policy.downloadsAllowed);
  const [allowlist, setAllowlist] = useState((policy.allowlist ?? []).join('\n'));
  const [blocklist, setBlocklist] = useState((policy.blocklist ?? []).join('\n'));
  const [saving, setSaving] = useState(false);

  const toList = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    setSaving(true);
    try {
      await updatePolicy({ allowExternal, downloadsAllowed, allowlist: toList(allowlist), blocklist: toList(blocklist) });
      toast.success('Policy saved');
      onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save policy'));
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">External browsing policy</h2>
      <div className="space-y-4">
        <label className="flex items-center gap-3">
          <Switch checked={allowExternal} onCheckedChange={setAllowExternal} />
          <span className="text-sm text-foreground">Allow employees to open external sites</span>
        </label>
        <label className="flex items-center gap-3">
          <Switch checked={downloadsAllowed} onCheckedChange={setDownloadsAllowed} />
          <span className="text-sm text-foreground">Allow downloads from external sites</span>
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Allowlist (one domain per line)</Label>
            <Textarea rows={4} value={allowlist} onChange={(e) => setAllowlist(e.target.value)} placeholder={'nmbbank.co.tz\ncrdbbank.co.tz'} />
            <p className="mt-1 text-xs text-muted-foreground">If set, only these domains (and subdomains) can be opened.</p>
          </div>
          <div>
            <Label>Blocklist (one domain per line)</Label>
            <Textarea rows={4} value={blocklist} onChange={(e) => setBlocklist(e.target.value)} placeholder={'example-bad.com'} />
            <p className="mt-1 text-xs text-muted-foreground">These domains are always blocked.</p>
          </div>
        </div>
        <Button disabled={saving} onClick={save}>Save policy</Button>
      </div>
    </Card>
  );
};

function safeHost(url: string): string | null {
  try { return new URL(url).hostname; } catch { return null; }
}

export default WebSettingsPage;
